import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { timesheetService, TimesheetEntryUI } from '../../services/timesheetService'
import { taskService } from '../../services/taskService'
import { dataService } from '../../services/dataService'
import { Check, X, Clock, AlertTriangle, ChevronDown, ChevronUp } from '../icons/Icons'
import s from './shared.module.css'

interface BudgetWarning {
  task_id: string
  task_title: string
  submitted_hours: number
  estimated_hours: number
}

interface PendingApproval {
  id: string
  org_id: string
  period_start: string
  period_end: string
  status: string
  submitted_by: string | null
  submitted_at: string | null
  rejected_reason: string | null
  submitter_name: string | null
  submitter_email: string | null
  resource_title: string | null
  resource_id: string | null
  total_hours: number
  budget_warnings: BudgetWarning[]
}

interface DetailRow {
  project_id: string
  project_name: string
  task_id?: string
  task_name: string
  is_billable: boolean
  monday: number
  tuesday: number
  wednesday: number
  thursday: number
  friday: number
  saturday: number
  sunday: number
  monday_comment?: string
  tuesday_comment?: string
  wednesday_comment?: string
  thursday_comment?: string
  friday_comment?: string
  saturday_comment?: string
  sunday_comment?: string
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export default function TimesheetApprovals() {
  const { organization, user } = useAuth()
  const orgId = organization!.id

  const [pending, setPending] = useState<PendingApproval[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [detailRows, setDetailRows] = useState<{ [periodId: string]: DetailRow[] }>({})
  const [detailLoading, setDetailLoading] = useState<string | null>(null)
  const [projects, setProjects] = useState<{ id: string; title: string }[]>([])

  useEffect(() => {
    dataService.getProjects(orgId).then((p: any[]) => setProjects(p)).catch(() => {})
  }, [orgId])

  const loadPending = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await timesheetService.getPendingApprovals(orgId)
      setPending(data as unknown as PendingApproval[])
    } catch (err: any) {
      setError(err?.message || 'Failed to load pending approvals')
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    loadPending()
  }, [loadPending])

  const handleApprove = async (periodId: string) => {
    setActionLoading(periodId)
    setError('')
    setSuccess('')
    try {
      await timesheetService.approveTimesheet(periodId, user!.id, user!.id)
      setSuccess('Timesheet approved successfully.')
      setTimeout(() => setSuccess(''), 3000)
      await loadPending()
    } catch (err: any) {
      setError(err?.message || 'Failed to approve timesheet')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (periodId: string) => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection.')
      return
    }
    setActionLoading(periodId)
    setError('')
    setSuccess('')
    try {
      await timesheetService.rejectTimesheet(periodId, user!.id, user!.id, rejectReason.trim())
      setSuccess('Timesheet rejected and returned to submitter.')
      setTimeout(() => setSuccess(''), 3000)
      setRejectingId(null)
      setRejectReason('')
      await loadPending()
    } catch (err: any) {
      setError(err?.message || 'Failed to reject timesheet')
    } finally {
      setActionLoading(null)
    }
  }

  const handleToggleDetails = async (item: PendingApproval) => {
    if (expandedId === item.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(item.id)
    if (!detailRows[item.id] && (item.submitted_by || item.resource_id)) {
      setDetailLoading(item.id)
      try {
        const startDate = item.period_start.split('T')[0]
        const endDate = item.period_end.split('T')[0]
        let entries: TimesheetEntryUI[] = []
        if (item.resource_id) {
          entries = await timesheetService.getTimesheetEntriesByResource(item.resource_id, startDate, endDate)
        } else if (item.submitted_by) {
          entries = await timesheetService.getTimesheetEntries(item.submitted_by, startDate, endDate)
        }
        const taskIds = [...new Set(entries.map(e => e.task_id).filter(Boolean) as string[])]
        const taskMap: { [id: string]: string } = {}
        await Promise.all(taskIds.map(async tid => {
          try {
            const task = await taskService.getTaskById(tid)
            if (task) taskMap[tid] = task.title
          } catch { /* ignore */ }
        }))
        const rows = buildDetailRows(entries, taskMap)
        setDetailRows(prev => ({ ...prev, [item.id]: rows }))
      } catch {
        setDetailRows(prev => ({ ...prev, [item.id]: [] }))
      } finally {
        setDetailLoading(null)
      }
    }
  }

  const buildDetailRows = (entries: TimesheetEntryUI[], taskMap: { [id: string]: string }): DetailRow[] => {
    const grouped: { [key: string]: TimesheetEntryUI[] } = {}
    entries.forEach(entry => {
      const key = `${entry.project_id}-${entry.task_id || 'none'}-${entry.is_billable ? 'b' : 'nb'}`
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(entry)
    })

    return Object.values(grouped).map(group => {
      const first = group[0]
      const row: DetailRow = {
        project_id: first.project_id,
        project_name: projects.find(p => p.id === first.project_id)?.title || 'Unknown',
        task_id: first.task_id,
        task_name: first.task_id ? (taskMap[first.task_id] || '') : '',
        is_billable: first.is_billable,
        monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0,
      }
      group.forEach(entry => {
        // Parse as local date parts to avoid UTC-midnight timezone shift
        const [ey, em, ed] = entry.entry_date.split('T')[0].split('-').map(Number)
        const date = new Date(ey, em - 1, ed)
        const dayIdx = (date.getDay() + 6) % 7
        const dayKey = DAY_KEYS[dayIdx]
        row[dayKey] += entry.hours
        const commentKey = `${dayKey}_comment` as keyof DetailRow
        if (entry.description && !(row[commentKey] as string)) {
          (row as any)[commentKey] = entry.description
        }
      })
      return row
    })
  }

  const getRowTotal = (row: DetailRow): number =>
    row.monday + row.tuesday + row.wednesday + row.thursday + row.friday + row.saturday + row.sunday

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  }

  const getWeekDateLabels = (periodStart: string): string[] => {
    const start = new Date(periodStart)
    return DAY_LABELS.map((_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    })
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading pending approvals...</div>
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Timesheet Approvals</h2>
          <p className={s.pageSubtitle}>
            {pending.length === 0
              ? 'No timesheets awaiting approval.'
              : `${pending.length} timesheet${pending.length === 1 ? '' : 's'} awaiting approval.`}
          </p>
        </div>
        <button className={s.secondaryBtn} onClick={loadPending}>Refresh</button>
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, color: '#842029' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: 4, color: '#0f5132' }}>
          {success}
        </div>
      )}

      {pending.length === 0 ? (
        <div className={s.card} style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>
          <Clock size={40} style={{ color: '#c8c6c4', marginBottom: 12 }} />
          <p style={{ fontSize: 14 }}>All caught up. No timesheets are waiting for approval.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pending.map(item => {
            const warnings: BudgetWarning[] = Array.isArray(item.budget_warnings) ? item.budget_warnings : []
            const isExpanded = expandedId === item.id
            const rows = detailRows[item.id]
            const dateLabels = getWeekDateLabels(item.period_start)
            return (
              <div key={item.id} className={s.card} style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#323130' }}>
                        {item.resource_title || item.submitter_name || item.submitter_email || 'Unknown user'}
                      </span>
                      <span style={{ padding: '2px 8px', backgroundColor: '#fff4ce', color: '#d13438', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                        Pending
                      </span>
                    </div>
                    {item.resource_title && item.submitter_name && (
                      <div style={{ fontSize: 12, color: '#8a8888', marginBottom: 6 }}>
                        Submitted by {item.submitter_name}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 24px', fontSize: 13, color: '#605e5c' }}>
                      <span><strong>Period:</strong> {formatDate(item.period_start)} — {formatDate(item.period_end)}</span>
                      <span><strong>Submitted:</strong> {formatDateTime(item.submitted_at)}</span>
                      <span><strong>Total hours:</strong> {Number(item.total_hours).toFixed(2)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button
                      className={s.secondaryBtn}
                      onClick={() => handleToggleDetails(item)}
                    >
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {isExpanded ? 'Hide Details' : 'View Details'}
                    </button>
                    <button
                      className={s.primaryBtn}
                      onClick={() => handleApprove(item.id)}
                      disabled={actionLoading === item.id}
                      style={{ backgroundColor: '#107c10' }}
                    >
                      <Check size={14} /> {actionLoading === item.id && rejectingId !== item.id ? '...' : 'Approve'}
                    </button>
                    <button
                      className={s.secondaryBtn}
                      onClick={() => { setRejectingId(rejectingId === item.id ? null : item.id); setRejectReason('') }}
                      disabled={actionLoading === item.id}
                    >
                      <X size={14} /> Reject
                    </button>
                  </div>
                </div>

                {warnings.length > 0 && (
                  <div style={{ marginTop: 12, padding: 12, backgroundColor: '#fff4ce', border: '1px solid #ffe69c', borderRadius: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <AlertTriangle size={16} style={{ color: '#997404' }} />
                      <strong style={{ color: '#664d03', fontSize: 13 }}>Budget Overrun Warning</strong>
                    </div>
                    <div style={{ fontSize: 12, color: '#664d03', lineHeight: 1.6 }}>
                      The following tasks have exceeded their estimated hours:
                      <ul style={{ margin: '6px 0 0 20px', padding: 0 }}>
                        {warnings.map(w => (
                          <li key={w.task_id}>
                            <strong>{w.task_title}</strong>: {Number(w.submitted_hours).toFixed(1)}h submitted vs {Number(w.estimated_hours).toFixed(1)}h estimated
                            <span style={{ color: '#d13438', fontWeight: 600 }}> ({(Number(w.submitted_hours) - Number(w.estimated_hours)).toFixed(1)}h over)</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {isExpanded && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #edebe9' }}>
                    {detailLoading === item.id ? (
                      <div style={{ padding: 24, textAlign: 'center', color: '#6c757d', fontSize: 13 }}>Loading daily breakdown...</div>
                    ) : rows && rows.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: 140 }}>Project</th>
                              <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: 120 }}>Task</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 60 }}>Billable</th>
                              {DAY_LABELS.map((day, i) => (
                                <th key={day} style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 64 }}>
                                  <div>{day}</div>
                                  <div style={{ fontSize: 10, fontWeight: 400, color: '#8a8888' }}>{dateLabels[i]}</div>
                                </th>
                              ))}
                              <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: 60, fontWeight: 700 }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, ri) => (
                              <React.Fragment key={ri}>
                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '6px 10px', fontWeight: 600 }}>{row.project_name}</td>
                                  <td style={{ padding: '6px 10px', color: '#605e5c' }}>{row.task_name || '—'}</td>
                                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                    <input type="checkbox" checked={row.is_billable} disabled style={{ opacity: 0.7 }} />
                                  </td>
                                  {DAY_KEYS.map(day => {
                                    const val = row[day] as number
                                    return (
                                      <td key={day} style={{ padding: '6px 6px', textAlign: 'center', color: val > 0 ? '#323130' : '#c8c6c4' }}>
                                        {val > 0 ? val.toFixed(2) : '—'}
                                      </td>
                                    )
                                  })}
                                  <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 700 }}>{getRowTotal(row).toFixed(2)}</td>
                                </tr>
                                <tr>
                                  <td colSpan={3} style={{ padding: '2px 10px 6px', fontSize: 11, color: '#8a8888' }}>Comments</td>
                                  {DAY_KEYS.map(day => {
                                    const commentKey = `${day}_comment` as keyof DetailRow
                                    const comment = row[commentKey] as string | undefined
                                    return (
                                      <td key={day} style={{ padding: '2px 6px 6px', fontSize: 11, color: '#8a8888', textAlign: 'center' }}>
                                        {comment || ''}
                                      </td>
                                    )
                                  })}
                                  <td></td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ padding: 24, textAlign: 'center', color: '#6c757d', fontSize: 13 }}>No detailed entries found for this period.</div>
                    )}
                  </div>
                )}

                {rejectingId === item.id && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #edebe9' }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Reason for rejection:
                    </label>
                    <textarea
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="Explain why this timesheet is being rejected..."
                      style={{ width: '100%', minHeight: 60, padding: 8, border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                      <button className={s.secondaryBtn} onClick={() => { setRejectingId(null); setRejectReason('') }}>Cancel</button>
                      <button
                        className={s.primaryBtn}
                        onClick={() => handleReject(item.id)}
                        disabled={actionLoading === item.id || !rejectReason.trim()}
                        style={{ backgroundColor: '#d13438' }}
                      >
                        {actionLoading === item.id ? '...' : 'Confirm Reject'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}